import { useEffect, useRef } from 'react';

interface IJotformEmbedProps {
  src: string;
  className?: string;
  scrolling?: boolean;
  frameBorder?: string | number;
}

const DEFAULT_STYLE: React.CSSProperties = { width: '100%', height: '300px' };

/**
 * React-19-compatible replacement for the unmaintained `react-jotform-embed`
 * package (which uses removed string refs, `componentWillMount`, and
 * `ReactDOM.findDOMNode`). Behavior mirrors the original: listen for postMessage
 * events from Jotform and resize / scroll / reload accordingly, then echo back
 * the host page URL info.
 */
export function JotformEmbed({ src, className, scrolling, frameBorder }: IJotformEmbedProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const handleMessage = (e: MessageEvent) => {
      const iframe = iframeRef.current;
      if (!iframe || typeof e.data !== 'string' || !e.data.split) return;

      const args = e.data.split(':');
      const formId = args[2];
      if (formId && !src.split('?')[0].endsWith(formId)) return;

      switch (args[0]) {
        case 'scrollIntoView':
          iframe.scrollIntoView();
          break;
        case 'setHeight':
          iframe.style.height = `${args[1]}px`;
          break;
        case 'collapseErrorPage':
          if (iframe.clientHeight > window.innerHeight) {
            iframe.style.height = `${window.innerHeight}px`;
          }
          break;
        case 'reloadPage':
          window.location.reload();
          break;
      }

      if (e.origin.indexOf('jotform') > -1 && iframe.contentWindow) {
        const urls = {
          docurl: encodeURIComponent(document.URL),
          referrer: encodeURIComponent(document.referrer),
        };
        iframe.contentWindow.postMessage(
          JSON.stringify({ type: 'urls', value: urls }),
          '*',
        );
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [src]);

  return (
    <iframe
      ref={iframeRef}
      title="jotform"
      style={DEFAULT_STYLE}
      className={className}
      src={src}
      frameBorder={frameBorder ?? 0}
      scrolling={scrolling ? 'yes' : 'no'}
    />
  );
}
